import _ from 'lodash'
import { PROPOSAL_REVIEW_STATUS, PROPOSAL_TYPE, PROPOSAL_TYPE_MAP } from '@vzmi/shp_shared_compo/constants/proposal'
import safeAwait from '../../../../lib/safeAwait'
import errorHelper, { httpHandler } from '../../../../lib/errorHelper'
import {
  getConvertedWarrantyListing,
  getConvertedCopyAndWarrantyListingParams
} from '../../BatchProposal/UpdateCopy/helper'
import {
  getConvertedImageListingParams,
  getListingWithImagesModels
} from '../../BatchProposal/UpdateImage/helper'
import {
  getConvertedAttributeListing,
  getUpdatedFormListingWithAttributes,
  getConvertedAttributeListingParams,
  getListingsFieldErrors
} from '../../BatchProposal/UpdateAttribute/helper'
import { getCombindedModelsListingFromProposal } from '../../BatchProposal/helper/batch'
import fetchBatchProposals from '../../BatchProposal/request/fetchBatchProposals'
import {
  UPDATE_IMAGE_V2,
  UPDATE_LISTING_ATTRIBUTE
} from '../UpdateListing/helpers/converters'
import { COMMA } from '@vzmi/component_ec_nodejs_util/dist/lib/parserUtil'
import { normalizeFeatureTitle } from '../Common/ProposalForm/Shared/normalizers'
import { API_ERROR_MAPPING, OTHER_BRAND_ID } from '../../../constants/globalTypes'

export const ANCHOR_KEY = {
  UPDATE_IMAGE: UPDATE_IMAGE_V2,
  UPDATE_ATTRIBUTE: UPDATE_LISTING_ATTRIBUTE,
  UPDATE_VIDEO: PROPOSAL_TYPE.UPDATE_VIDEO,
  UPDATE_COPY: PROPOSAL_TYPE.UPDATE_COPY
}

export const ANCHOR_MAP = {
  UPDATE_IMAGE: {
    link: ANCHOR_KEY.UPDATE_IMAGE,
    linkTitle: '賣場與圖片',
    sectionTitle: '賣場與圖片'
  },
  UPDATE_ATTRIBUTE: {
    link: ANCHOR_KEY.UPDATE_ATTRIBUTE,
    linkTitle: '商品規格',
    sectionTitle: '商品規格'
  },
  UPDATE_VIDEO: {
    link: ANCHOR_KEY.UPDATE_VIDEO,
    linkTitle: '賣場影片',
    sectionTitle: '賣場影片'
  },
  UPDATE_COPY: {
    link: ANCHOR_KEY.UPDATE_COPY,
    linkTitle: '商品詳情與保證',
    sectionTitle: '商品詳情與保證'
  }
}

export const ANCHORS = [
  ANCHOR_MAP.UPDATE_IMAGE,
  ANCHOR_MAP.UPDATE_ATTRIBUTE,
  ANCHOR_MAP.UPDATE_COPY,
  ANCHOR_MAP.UPDATE_VIDEO
]

export const UPDATE_PROPOSAL_TYPE = {
  [PROPOSAL_TYPE.UPDATE_VIDEO]: PROPOSAL_TYPE.UPDATE_VIDEO,
  [PROPOSAL_TYPE.UPDATE_COPY]: PROPOSAL_TYPE.UPDATE_COPY,
  [PROPOSAL_TYPE.UPDATE_LISTING]: PROPOSAL_TYPE.UPDATE_LISTING,
  [UPDATE_IMAGE_V2]: PROPOSAL_TYPE.UPDATE_IMAGE,
  [UPDATE_LISTING_ATTRIBUTE]: PROPOSAL_TYPE.UPDATE_ATTRIBUTE
}

export const UPDATE_PROPOSAL_TYPE_MAP = {
  // spec 要求特殊提案名稱
  [UPDATE_IMAGE_V2]: '圖片',
  [UPDATE_LISTING_ATTRIBUTE]: ANCHOR_MAP.UPDATE_ATTRIBUTE.sectionTitle,
  [PROPOSAL_TYPE.UPDATE_VIDEO]: ANCHOR_MAP.UPDATE_VIDEO.sectionTitle,
  [PROPOSAL_TYPE.UPDATE_COPY]: ANCHOR_MAP.UPDATE_COPY.sectionTitle,
  [PROPOSAL_TYPE.UPDATE_LISTING]: PROPOSAL_TYPE_MAP[PROPOSAL_TYPE.UPDATE_LISTING]
}

export const BRAND_ID_ERROR_NOTICE = {
  REFRESH_PAGE: '無法取得商品品牌列表，請重新整理頁面',
  HAVE_BRAND_INPUT_IS_EMPTY: '規格表中尚未選擇品牌，請先選擇品牌',
  HAVE_BRAND_INPUT_NO_FOUND_OPTION: '商品品牌中找不到相同的項目，為您選擇「其他品牌」',
  NO_FOUND_BRAND_INPUT_HAVE_CATITEM: '規格表中找不到品牌欄位，為您選擇「其他品牌」',
  NO_FOUND_BRAND_INPUT: '規格表中找不到品牌欄位，請先選擇規格表'
}

// 提案站別／對象
// ex: (10)流行女裝-高OX(oxpolly)
export const getSubStationLabel = (proposalSetting, subStationId) => {
  const subStationLabel = _.flow(
    () => _.get(proposalSetting, 'subStations', []),
    subStations => _.find(subStations, { categoryId: subStationId }),
    (subStation = {}) => {
      if (_.isEmpty(subStation)) {
        return ''
      }

      const {
        categoryName,
        id,
        name
      } = subStation
      const subId = _.replace(subStationId, 'sub', '')
      return `(${subId})${categoryName}-${name}(${id})`
    }
  )()
  return subStationLabel
}

// 判斷是否為雙層屬性賣場
export const getIsTwoLayerListing = models => {
  const isTwoLayerListing = !_.isUndefined(
    _.get(models, '[0].items[0].sku')
  )
  return isTwoLayerListing
}

export const getValidDefaultValueListing = listing => {
  const {
    featureTitle = ''
  } = listing
  const convertedFeatureTitle = normalizeFeatureTitle(featureTitle)
  return {
    ...listing,
    featureTitle: convertedFeatureTitle
  }
}

// 為了展開商品 models 配合 ImageFunctionTable 結構在雙層賣場時不合併第二層屬性
export const getListingWithSpreadModels = listing => {
  const {
    models = []
  } = listing
  const isTwoLayerListing = getIsTwoLayerListing(models)
  // 無屬性、單層屬性賣場 models 沒有 items 不需要額外處理
  if (!isTwoLayerListing) {
    const modelsWithIndex = _.map(models, (model, index) => ({ ...model, index }))
    return { ...listing, spreadModels: modelsWithIndex }
  }

  // 雙層屬性賣場會將第二層展開
  const spreadModels = []
  for (const model of models) {
    const items = _.get(model, 'items', [])
    for (const [index, item] of items.entries()) {
      spreadModels.push({ ...model, index, items: [item] })
    }
  }
  return {
    ...listing,
    spreadModels
  }
}

// 按照無屬性、單層、雙層屬性賣場決定商品名稱的預設值的欄位位置
export const getListingWithProductName = listing => {
  const {
    models,
    spreadModels,
    skuDetail,
    isNoModelsListing,
    productName,
    partNo
  } = listing

  let spreadModelsWithProductName = []
  if (isNoModelsListing) {
    // 完成提案時 productName 從 listing 底下取得最新修改結果
    // 預設 productName 從 skuDetail.name 裡取得預設值
    const currentProductName = productName || _.get(skuDetail, 'name')
    spreadModelsWithProductName = _.map(spreadModels, model => {
      return {
        ...model,
        productName: currentProductName,
        partNo
      }
    })
  } else {
    // 單、雙層屬性時商品名稱從 models[x].name / models[x].items[x].name 裡取得預設值
    const isTwoLayerListing = getIsTwoLayerListing(models)
    spreadModelsWithProductName = _.map(spreadModels, model => {
      let productName = _.get(model, 'name')
      if (isTwoLayerListing) {
        productName = _.get(model, 'items[0].name')
      }

      return {
        ...model,
        productName
      }
    })
  }
  const listingWithProductName = {
    ...listing,
    spreadModels: spreadModelsWithProductName
  }
  return listingWithProductName
}

export const SHORT_DESCRIPTION_MAX_SIZE = 5
export const getListingWithMaxiumShortDesc = listing => {
  const {
    shortDescription = []
  } = listing
  const shortDescriptionSize = _.size(shortDescription)
  if (shortDescriptionSize === SHORT_DESCRIPTION_MAX_SIZE) {
    return listing
  }

  // 預設最多可以填 5 個商品特色，所以預設不足 5 個時幫他補上空字串
  // redux form 才會按照數量顯示 input
  let maxiumShortDescription
  if (shortDescriptionSize < SHORT_DESCRIPTION_MAX_SIZE) {
    maxiumShortDescription = [
      ...shortDescription,
      ..._.times(SHORT_DESCRIPTION_MAX_SIZE - shortDescriptionSize, () => '')
    ]
  } else {
    // 商品特色超過 5 組剩下的會併入第 5 組
    const seperatorIndex = SHORT_DESCRIPTION_MAX_SIZE - 1
    maxiumShortDescription = [
      ..._.slice(shortDescription, 0, seperatorIndex),
      _.join(_.slice(shortDescription, seperatorIndex), '')
    ]
  }

  return {
    ...listing,
    shortDescription: maxiumShortDescription
  }
}

export const getListingWithVideo = listing => {
  const {
    isNoModelsListing,
    models,
    videos
  } = listing
  if (isNoModelsListing) {
    // 無屬性賣場 video 會直接在 listing 底下
    const video = _.get(videos, '0', {})
    return {
      ...listing,
      video
    }
  }

  // 單、雙層屬性賣場 video 在 models 第一層裡
  const video = _.get(models, '0.videos[0]', {})
  return {
    ...listing,
    video
  }
}

const SECTION_SUFFIX = 'Visible'

const DEFAULT_INVISIBLE_SECTION = [
  ANCHOR_KEY.UPDATE_VIDEO
]

export const getSectionVisibleFieldName = sectionKey => `${sectionKey}${SECTION_SUFFIX}`

export const getListingWithSectionToggle = (listing, subProposalMapByType = {}) => {
  const defaultInvisibleSectionMap = _.keyBy(DEFAULT_INVISIBLE_SECTION)
  // 產生 redux form field 用來判斷 user 是否勾選同時修改區塊的欄位
  const toggleSectionKeys = _.map(ANCHORS, anchor => getSectionVisibleFieldName(anchor.link))
  // 預設全部區塊展開
  const toggleProps = _.reduce(toggleSectionKeys, (props, key) => {
    const anchorLink = _.replace(key, SECTION_SUFFIX, '')
    // subProposalMapByType 只有在 compose init 會是空物件
    // 只要表單產生完成最少會有 updateImage
    const defaultValue = _.isEmpty(subProposalMapByType)
      ? !(anchorLink in defaultInvisibleSectionMap)
      // 有找到指定 sub proposal 代表欄位有勾選同時修改
      : !_.isUndefined(_.get(subProposalMapByType, anchorLink))
    props[key] = defaultValue
    return props
  }, {})
  // user 選擇不修改區塊
  const listingToggleProps = _.pick(listing, toggleSectionKeys)
  return {
    ...listing,
    ...toggleProps,
    ...listingToggleProps
  }
}

export const getConvertedListing = (listing, subProposalMapByType) => {
  // 按照各個區塊將賣場資料轉換成 redux form 方便使用的格式
  const convertedListing = _.flow(
    getValidDefaultValueListing,
    // 先轉換規格表，因為 hasSpecLevel 會需要判斷是否為無屬性賣場
    // 修改圖片會將無屬性也轉換成單層 model 讓 component 讀取
    // updateAttribute
    getConvertedAttributeListing,
    // updateImage
    getListingWithImagesModels,
    getListingWithSpreadModels,
    getListingWithProductName,
    getListingWithMaxiumShortDesc,
    // updateVideo
    getListingWithVideo,
    // updateWarranty
    listing => getConvertedWarrantyListing(listing, true),
    // toggle section visible
    listing => getListingWithSectionToggle(listing, subProposalMapByType)
  )(listing)
  return convertedListing
}

export const getFormListingWithFilledAttributes = (listings, currentMenuData) => {
  const updatedListing = _.flow(
    // 賣場的規格項目含有預設值的填入值
    () => getUpdatedFormListingWithAttributes(listings, currentMenuData),
    _.first
  )()
  return updatedListing
}

// 根據 redux form 組出 post/put API 商品名稱與料號
// 並將兼容 ImageFunctionTable 的 spreadModels 重新組成 models 格式以 sku 為 key 的對應表
export const getModelsWithPartNoAndProductNameMapBySku = listing => {
  const {
    models,
    spreadModels,
    isNoModelsListing,
    sku
  } = listing
  let modelMapBySku = {}
  if (isNoModelsListing) {
    const {
      productName,
      partNo
    } = _.get(spreadModels, '0', {})
    const convertedParams = {
      ...(!_.isUndefined(productName) && { productName }),
      ...(!_.isUndefined(partNo) && { partNo })
    }
    if (_.isEmpty(convertedParams)) {
      return modelMapBySku
    }

    // 無屬性賣場商品名稱與料號會直接放 listing 底下
    modelMapBySku = { [sku]: convertedParams }
    return modelMapBySku
  }

  const isTwoLayerListing = getIsTwoLayerListing(models)
  if (isTwoLayerListing) {
    // 雙層屬性賣場會把相同第一層的 modelItem 重新合併到同一個第一層底下
    modelMapBySku = _.reduce(spreadModels, (map, spreadModel) => {
      const { sku: parentSku, items, productName } = spreadModel
      const { sku, spec, partNo } = _.get(items, '0', {})
      const convertedModelItem = {
        spec,
        ...(!_.isUndefined(sku) && { sku }),
        ...(!_.isUndefined(partNo) && { partNo }),
        ...(!_.isUndefined(productName) && { name: productName })
      }
      if (_.isEmpty(convertedModelItem)) {
        return map
      }

      if (parentSku in map) {
        map[parentSku].items.push(convertedModelItem)
      } else {
        map[parentSku] = { items: [convertedModelItem] }
      }
      return map
    }, {})
  } else {
    // 單層屬性賣場只需要組出商品名稱與料號
    modelMapBySku = _.reduce(spreadModels, (map, spreadModel) => {
      const { sku, spec, partNo, productName } = spreadModel
      const convertedModel = {
        spec,
        ...(!_.isUndefined(sku) && { sku }),
        ...(!_.isUndefined(partNo) && { partNo }),
        ...(!_.isUndefined(productName) && { name: productName })
      }
      if (_.isEmpty(convertedModel)) {
        return map
      }

      map[sku] = convertedModel
      return map
    }, {})
  }

  return modelMapBySku
}

export const getConvertedVideoListingParams = (listing, subProposalListing) => {
  const {
    video = {},
    isNoModelsListing,
    shareMediaBetweenModels,
    models: unconvertedModels
  } = listing
  const {
    models: convertedModels
  } = subProposalListing
  const { order, url } = video
  // 沒有影片、刪除既有影片回傳空陣列
  const videoParam = {
    videos: _.isUndefined(url) ? [] : [{ order, url }]
  }
  const isTwoLayerListing = getIsTwoLayerListing(convertedModels)
  if (isNoModelsListing) {
    return videoParam
  }

  const currentModels = _.isUndefined(convertedModels)
    // getParams 按照錨點順序會先在轉換 updateImage 時產生 convertedListing
    // 如果 convertedListing 沒有 models 代表沒有 update image 那就直接用賣場預設 models 寫入 videos
    ? unconvertedModels
    : convertedModels
  const modelsWithVideos = _.map(currentModels, (model, index) => {
    if (
      // 雙層賣場直接加影片欄位
      isTwoLayerListing ||
      (
        // 單層賣場
        !isTwoLayerListing && (
          // 共用圖片只有第一筆送影片欄位
          (index === 0 && shareMediaBetweenModels) ||
          // 非共用欄位每一筆都送影片欄位
          !shareMediaBetweenModels
        )
      )
    ) {
      return {
        ...model,
        ...videoParam
      }
    }
    return model
  })
  return { models: modelsWithVideos }
}

export const getListingWithMergedModels = (listing = {}) => {
  const {
    models: originModels = [],
    spreadModels = []
  } = listing
  const isTwoLayerListing = getIsTwoLayerListing(listing.models)
  let mergedModels = spreadModels
  if (isTwoLayerListing) {
    mergedModels = _.flow(
      () => _.groupBy(spreadModels, 'sku'),
      // 按照預設商品屬性排序重組已展開的商品
      modelsGroupBySku => _.map(originModels, (originModel = {}) => {
        const groupedModels = _.get(modelsGroupBySku, originModel.sku, [])
        const model = _.get(groupedModels, '0', {})
        const items = _.concat(..._.map(groupedModels, 'items'))
        return { ...model, items }
      })
    )()
  }
  return {
    ...listing,
    models: mergedModels
  }
}

// 按照錨點依序組出 submit proposal 會用到的 params
export const getSubPropsalsParams = (formValues, totalSubProposals) => {
  const {
    sku: skuFromListing,
    isNoModelsListing
  } = formValues
  const subProposalMapByType = _.keyBy(totalSubProposals, 'type')
  const subProposalTypes = []
  let subProposalListing = {}

  for (const anchor of ANCHORS) {
    const section = anchor.link
    // 同時修改欄位有勾選的區塊才會被組進 params
    const isUpdatableSection = _.get(formValues, getSectionVisibleFieldName(section))
    if (isUpdatableSection) {
      let subProposalType
      let sectionListing
      switch (section) {
        case ANCHOR_KEY.UPDATE_IMAGE: {
          subProposalType = UPDATE_IMAGE_V2
          // 將 spreadModels(實際上操作 UI 修改商品名稱、料號對應的欄位) 轉成 sku 對應表
          const modelMap = getModelsWithPartNoAndProductNameMapBySku(formValues)
          sectionListing = _.flow(
            () => getListingWithMergedModels(formValues),
            // 更新商品圖片
            listing => getConvertedImageListingParams(listing, true),
            imageListingParams => {
              // 無屬性商品名稱、料號放 listing 底下
              if (isNoModelsListing) {
                return {
                  ...imageListingParams,
                  ..._.get(modelMap, skuFromListing, {})
                }
              }

              const { models = [] } = imageListingParams
              // 單雙層屬性直接把 sku 對應表組好的商品名稱、料號對照 sku 寫入
              const convertedModels = _.map(models, model => {
                const { sku: modelSku } = model
                return {
                  ...model,
                  ..._.get(modelMap, modelSku, {})
                }
              })
              return {
                ...imageListingParams,
                models: convertedModels
              }
            }
          )()
          const { shortDescription = [], ...updateImageParams } = {
            ..._.pick(formValues, [
              // 賣場編號
              'id',
              // 賣場名稱
              'name',
              // 特色標題
              'featureTitle',
              // 商品特色
              'shortDescription',
              // 供應商商品料號
              'partNo',
              // 商品型號
              'model',
              // 共用同組商品圖
              'shareMediaBetweenModels',
              // 商品品牌
              'brandId',
              'brandName'
            ])
          }
          sectionListing = {
            ...sectionListing,
            ...updateImageParams,
            // 預設商品特色未滿 5 個表單以空字串填滿 5 個項目顯示輸入框，
            // 在送給 API 時再把空字串過濾掉
            shortDescription: _.filter(shortDescription, desc => !_.isEmpty(desc))
          }
          break
        }
        case ANCHOR_KEY.UPDATE_ATTRIBUTE: {
          subProposalType = UPDATE_LISTING_ATTRIBUTE
          sectionListing = getConvertedAttributeListingParams(formValues)
          break
        }
        case ANCHOR_KEY.UPDATE_VIDEO: {
          subProposalType = PROPOSAL_TYPE.UPDATE_VIDEO
          sectionListing = getConvertedVideoListingParams(formValues, subProposalListing)
          break
        }
        case ANCHOR_KEY.UPDATE_COPY: {
          subProposalType = PROPOSAL_TYPE.UPDATE_COPY

          const {
            copy,
            warranty
          } = getConvertedCopyAndWarrantyListingParams(formValues)
          sectionListing = {
            copy,
            warranty
          }
          break
        }
        default:
          break
      }

      if (!_.isUndefined(subProposalType)) {
        const id = _.get(subProposalMapByType, `${subProposalType}.id`)
        subProposalTypes.push({ id, type: subProposalType })
      }

      if (!_.isUndefined(sectionListing)) {
        subProposalListing = {
          ...subProposalListing,
          ...sectionListing
        }
      }
    }
  }

  const subPropsalsParams = {
    subProposals: subProposalTypes,
    listing: subProposalListing
  }
  return subPropsalsParams
}

// 取得 submit proposal 會用到的 params
export const getParams = (formValues, reviewStatus, totalSubProposals) => {
  const {
    applicant,
    proposal: {
      id: proposalId
    } = {}
  } = formValues
  const subPropsalsParams = getSubPropsalsParams(formValues, totalSubProposals)
  const updateParams = {
    reviewStatus,
    applicant,
    id: proposalId,
    ...subPropsalsParams
  }
  return updateParams
}

// 完成、送出提案
export const submitProposal = async (actions, params) => {
  const [mainError, payload] = await safeAwait(
    params.id
      ? actions.updateListingInfoProposal(params).then(httpHandler)
      : actions.createListingInfoProposal(params).then(httpHandler)
  )
  // 失敗時先返回錯誤訊息
  const errorMessage = errorHelper.parseErrorMessage(mainError)
  if (errorMessage) {
    // 配合 subProposals 可能有多個錯誤，回傳皆以 array 表示
    return [
      [{ message: errorMessage, params, error: mainError }]
    ]
  }

  const subProposals = _.get(payload, 'subProposals', [])
  // 沒有 id 代表該子提案沒有成功
  const failedSubProposalResults = _.flow(
    () => _.filter(subProposals, subProposal => _.isUndefined(subProposal.id)),
    failedSubProposals => _.map(failedSubProposals, failedSubProposal => {
      const subProposalErrors = _.get(failedSubProposal, 'errors')
      const convertedErrors = _.map(subProposalErrors, error => {
        const message = errorHelper.parseErrorMessage(error)
        // 轉換成單筆修改規格表的錯訊息格式
        const { code, field, invalidValue } = error
        return {
          code,
          invalidValue: `${field}: ${invalidValue}`,
          message: `[${code}] ${message}`,
          params,
          error
        }
      })
      return convertedErrors
    })
  )()
  // 母提案成功但部分子提案失敗時將子提案錯誤訊息抽出組稱錯誤訊息
  if (!_.isEmpty(failedSubProposalResults)) {
    return failedSubProposalResults
  }

  let subProposalMap = {}
  if (params.reviewStatus === PROPOSAL_REVIEW_STATUS.DRAFT) {
    // 因為 post updateListing 的回應母提案只會有 1000px size 的圖片
    // 所以完成提案時會去打 get updateImage proposal 取得最新修改過的圖片
    // 必定會有修改圖片
    const updateImageProposalId = _.get(
      _.find(subProposals, { type: UPDATE_IMAGE_V2 }),
      'id'
    )
    const ids = _.flow(
      () => [
        updateImageProposalId
      ],
      ids => _.filter(ids, id => !_.isUndefined(id)),
      filteredIds => _.join(filteredIds, COMMA)
    )()
    const proposalsParam = { id: ids }
    const [proposalsError, proposals] = await fetchBatchProposals(actions, proposalsParam)
    const errorMessage = errorHelper.parseErrorMessage(proposalsError)
    if (errorMessage) {
      return [
        [{
          message: errorMessage,
          params,
          error: proposalsError
        }]
      ]
    }

    subProposalMap = _.keyBy(proposals, 'type')
  }

  // 沒有錯誤時返回 proposal
  return [
    [undefined, payload, subProposalMap]
  ]
}

export const getFieldErrors = (listing, failedProposalMap) => {
  // 一般 listing 底下非巢狀欄位這裡會處理 ex: listing.copy / listing.attributeDisplayMode
  const commonFieldError = _.flow(
    () => _.get(failedProposalMap, listing.id),
    fieldError => {
      // proposal.listing.productName: aa1234 > productName
      const field = _.replace(
        (
          _.get(fieldError, 'error.field') ||
          _.get(fieldError, 'error.detail[0].path') ||
          ''
        ),
        /.*listing\./,
        ''
      ).replace(/:.+/, '')
      if (_.isUndefined(fieldError) || _.isEmpty(field)) {
        return {}
      }

      const message = _.get(fieldError, 'message')
      return { [field]: message }
    }
  )()
  let listingAttributeFieldErrors = {}
  if (_.isEmpty(commonFieldError)) {
    const errorInfo = _.get(failedProposalMap, listing.id)
    const postingProposal = _.get(errorInfo, 'params')
    const errorCode = _.get(errorInfo, 'error.code')
    const originalMessage = _.get(errorInfo, 'message')
    const apiErrorMessage = errorCode ? _.get(API_ERROR_MAPPING, errorCode.toString()) : null
    const message = apiErrorMessage || originalMessage
    const invalidValue = _.get(errorInfo, 'error.invalidValue', '')

    if (postingProposal && invalidValue && invalidValue.includes('attributes[')) {
      const match = invalidValue.match(/attributes\[(\d+)\]/)
      if (match && match[1]) {
        const payloadErrorIndex = match[1]
        const errorAttributeName = _.get(postingProposal, `listing.attributes[${payloadErrorIndex}].name`)

        if (errorAttributeName) {
          const completeAttributes = _.unionBy(
            listing.attributes,
            _.get(postingProposal, 'listing.attributes', []),
            'name'
          )

          const currentFormErrorIndex = _.findIndex(completeAttributes, { name: errorAttributeName })

          if (currentFormErrorIndex > -1) {
            const newErrors = {}
            _.set(newErrors, `attributes[${currentFormErrorIndex}].selectedValues`, message)
            listingAttributeFieldErrors = newErrors
          }
        }
      }
    } else {
      const listingsFieldErrors = getListingsFieldErrors([listing], failedProposalMap)
      // 批次系列因為有多筆賣場所以 redux form 會用 listings
      // listings[0].attributes[17].selectedValues
      // 轉換成單筆的修改賣場資訊的規格項目欄位
      // attributes[17].selectedValues
      listingAttributeFieldErrors = _.first(listingsFieldErrors.listings)
    }
  }
  const fieldErrors = {
    ...commonFieldError,
    ...listingAttributeFieldErrors
  }
  return fieldErrors
}

// 從既有賣場與修改後的提案取得目前最新賣場資訊用於顯示確認、檢視頁
export const getListingWithProposal = (listing, batchResult, subProposalMap) => {
  const propsPickFromProposal = [
    'id',
    'reviewStatus',
    'executeStatus',
    'executedTs',
    'executeResult',
    'declineReason',
    'declineDetail',
    'suggestion',
    'subProposals'
  ]
  // submit 成功修改賣場返回的資料
  const successProposal = _.get(batchResult, `successProposalMap.${listing.id}`, {})
  const proposal = _.pick(successProposal, propsPickFromProposal)
  const listingFromProposal = _.get(successProposal, 'listing', {})
  // proposal listing 的 models 只會回傳「修改過」的 models
  // 所以需要和現有的 listing models 合併才能得到完整商品資料
  const updateImageProposal = _.get(subProposalMap, UPDATE_IMAGE_V2, successProposal)
  const noModelsListingImages = _.get(updateImageProposal, 'listing.images')
  const noModelsListingFromProposal = {
    ...(!_.isUndefined(noModelsListingImages) && { images: noModelsListingImages })
  }
  // 組出非無屬性賣場商品欄位
  const combindedModelsListingFromProposal = _.flow(
    // 從母提案組出的商品名稱、料號和影片
    () => getCombindedModelsListingFromProposal(
      successProposal,
      listing
    ),
    // 從修改圖片子提案組出既有加上修改過的圖片
    convertedListing => getCombindedModelsListingFromProposal(
      // proposal 要拿修改圖片子提案才能拿到完整的 models images，母提案只會回傳 1000 x 1000 導致修改尺寸異常
      // 萬一沒找到修改圖片子提案則改回拿母提案的避免畫面異常
      updateImageProposal,
      convertedListing
    )
  )()
  return {
    ...listing,
    ...listingFromProposal,
    ...noModelsListingFromProposal,
    models: _.get(combindedModelsListingFromProposal, 'models', []),
    proposal
  }
}

export const getUpdatedSubProposals = (prevSubProposals, listingWithProposal) => {
  const prevSubProposalMapByType = _.keyBy(prevSubProposals, 'type')
  const newSubProposals = _.flow(
    () => _.get(listingWithProposal, 'proposal.subProposals', []),
    // 找出沒有送出過的提案
    subProposals => _.filter(subProposals, subProposal => !(subProposal.type in prevSubProposalMapByType))
  )()
  // 合併已送出過提案編號與新送出提案編號
  const updatedSubProposals = [...prevSubProposals, ...newSubProposals]
  return updatedSubProposals
}

export const getBrandOption = (selectedBrandValue, options) => {
  return options.find(option => _.toUpper(option.label) === _.toUpper(selectedBrandValue))
}

export const getNoticeAndBrandId = (listing, options) => {
  const listingBrandAttribute = _.find(_.get(listing, 'attributes', []), (i) => i.name === '品牌')
  const listingCatItemId = _.get(listing, 'catItemId')
  const noBrand = _.isEmpty(listingBrandAttribute)
  const noMatch = _.isEmpty(listingCatItemId)

  if (noBrand) {
    return { notice: BRAND_ID_ERROR_NOTICE.NO_FOUND_BRAND_INPUT, isErrorNotice: true }
  }
  if (noMatch) {
    return {
      notice: BRAND_ID_ERROR_NOTICE.NO_FOUND_BRAND_INPUT_HAVE_CATITEM,
      brandId: OTHER_BRAND_ID,
      isErrorNotice: true
    }
  }

  const selectedBrand = _.get(listingBrandAttribute, 'selectedValues[0]')
  const matchBrandOption = getBrandOption(selectedBrand, options)

  if (_.isEmpty(selectedBrand)) {
    return { notice: BRAND_ID_ERROR_NOTICE.HAVE_BRAND_INPUT_IS_EMPTY, isErrorNotice: true }
  }

  if (!matchBrandOption) {
    return {
      notice: BRAND_ID_ERROR_NOTICE.HAVE_BRAND_INPUT_NO_FOUND_OPTION,
      brandId: OTHER_BRAND_ID,
      isErrorNotice: false
    }
  }

  return { brandId: matchBrandOption.value }
}

// for newlisting clonelsting don't has selectvalue
export const getNoticeAndBrandIdForSpec = (attributes, catItemId, options) => {
  const listingBrandAttribute = _.find(attributes, (i) => i.name === '品牌')
  const noBrand = _.isEmpty(listingBrandAttribute)
  const noMatch = _.isEmpty(catItemId)
  if (noBrand) {
    return { notice: BRAND_ID_ERROR_NOTICE.NO_FOUND_BRAND_INPUT, isErrorNotice: true }
  }
  if (noMatch) {
    return {
      notice: BRAND_ID_ERROR_NOTICE.NO_FOUND_BRAND_INPUT_HAVE_CATITEM,
      brandId: OTHER_BRAND_ID,
      isErrorNotice: true
    }
  }

  const selectedBrand = _.get(listingBrandAttribute, 'values[0]')
  const matchBrandOption = getBrandOption(selectedBrand, options)

  if (_.isEmpty(selectedBrand)) {
    return { notice: BRAND_ID_ERROR_NOTICE.HAVE_BRAND_INPUT_IS_EMPTY, isErrorNotice: true }
  }

  if (!matchBrandOption) {
    return {
      notice: BRAND_ID_ERROR_NOTICE.HAVE_BRAND_INPUT_NO_FOUND_OPTION,
      brandId: OTHER_BRAND_ID,
      isErrorNotice: false
    }
  }

  return { brandId: matchBrandOption.value }
}
